import React, { useState, useEffect } from 'react'
import { NavLink, Link } from 'react-router-dom'
import axios from 'axios'
import Preloader from '../preloader/Preloader'
import swal from 'sweetalert2'
import $ from 'jquery'
import 'datatables.net'

/* ---------- imports ---------- */
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

import { CSVLink } from 'react-csv' // For CSV download
import GoogleMapComponent from 'src/components/google_maps/GoogleMapComponent'
import * as XLSX from 'xlsx'
import { getXeroContactsArray, refreshXeroContactsCache } from 'src/services/xeroContactsCache'

const XERO_CONTACT_REFRESH_KEY = 'xero_contacts_last_manual_refresh'
const XERO_CONTACT_REFRESH_COOLDOWN_MS = 24 * 60 * 60 * 1000

function Customers() {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [retryKey, setRetryKey] = useState(0)
  const [refreshAvailableAt, setRefreshAvailableAt] = useState(() => {
    const lastRefresh = Number(localStorage.getItem(XERO_CONTACT_REFRESH_KEY) || 0)
    const availableAt = lastRefresh + XERO_CONTACT_REFRESH_COOLDOWN_MS
    return availableAt > Date.now() ? availableAt : 0
  })
  const [customerList, setCustomerList] = useState([])
  const [xeroCustomerList, setXeroCustomerList] = useState([])
  const [addressData, setAddressData] = useState([])
  const [refreshingXeroContacts, setRefreshingXeroContacts] = useState(false)

  useEffect(() => {
    let active = true

    const loadCustomers = async () => {
      setLoading(true)
      setLoadError('')

      const [customersResult, addressesResult] = await Promise.allSettled([
        axios.get('/api/show_customers', { timeout: 15000 }),
        axios.get('/api/show_address', { timeout: 15000 }),
      ])

      if (!active) return

      const errors = []

      if (customersResult.status === 'fulfilled' && customersResult.value.data.status === 200) {
        const data = customersResult.value.data
        setCustomerList(data.customer || [])

        const contacts = data.xeroContact?.body?.Contacts
        if (Array.isArray(contacts)) {
          setXeroCustomerList(
            contacts.filter((contact) => contact.IsCustomer === true).slice(0, 5000),
          )
        } else {
          errors.push('Xero customer contacts could not be loaded.')
        }
      } else {
        errors.push('Customers could not be loaded.')
      }

      if (addressesResult.status === 'fulfilled' && addressesResult.value.data.status === 200) {
        const transformedData = (addressesResult.value.data.address || []).map((item) => ({
          id: item.id || '',
          customer_address_id: item?.customer_address_id || '',
          address: item.address || '',
          street: item.street || '',
          city: item.city || '',
          state: item.state || '',
          post_code: item.post_code || '',
          del_zone: item.del_zone || '',
          delivery_zone_id: item.delivery_zones?.id || '',
          delivery_zone_name: item.delivery_zones?.name || '',
          location_note: item.location_note || '',
          p_location: item.p_location || '',
          resident_contact: item.resident_contact || '',
          resident_contact_two: item.resident_contact_two || '',
          resident_name: item.resident_name || '',
          resident_name_two: item.resident_name_two || '',
          created_at: item.created_at || '',
          updated_at: item.updated_at || '',
        }))

        setAddressData(transformedData)
      } else {
        errors.push('Customer addresses could not be loaded.')
      }

      setLoadError(errors.join(' '))
      setLoading(false)

      setTimeout(() => {
        if ($.fn.DataTable.isDataTable('#dataTable')) return

        $('#dataTable').DataTable({
          scrollX: false,
          pageLength: 100,
        })
      }, 10)
    }

    loadCustomers()

    return () => {
      active = false
    }
  }, [retryKey])

  const refreshXeroContacts = async () => {
    const now = Date.now()
    const availableAt = Number(localStorage.getItem(XERO_CONTACT_REFRESH_KEY) || 0)

    if (availableAt > now) {
      setRefreshAvailableAt(availableAt)
      return
    }

    const nextAvailableAt = now + XERO_CONTACT_REFRESH_COOLDOWN_MS
    localStorage.setItem(XERO_CONTACT_REFRESH_KEY, String(now))
    setRefreshAvailableAt(nextAvailableAt)
    setRefreshingXeroContacts(true)

    try {
      const xeroData = await refreshXeroContactsCache()
      const refreshedCustomers = getXeroContactsArray(xeroData).filter(
        (contact) => contact.IsCustomer === true,
      )
      const renamedCustomers = refreshedCustomers.filter((contact) => {
        const localCustomer = customerList.find(
          (customer) => customer.contact_id === contact.ContactID,
        )

        return (
          localCustomer &&
          String(localCustomer.account_name || '').trim() !== String(contact.Name || '').trim()
        )
      })

      for (const contact of renamedCustomers) {
        const localCustomer = customerList.find(
          (customer) => customer.contact_id === contact.ContactID,
        )

        await axios.put(`/api/update_customers/${contact.ContactID}`, {
          ...localCustomer,
          account_name: contact.Name,
        })
      }

      setXeroCustomerList(refreshedCustomers)
      setCustomerList((customers) =>
        customers.map((customer) => {
          const contact = refreshedCustomers.find((item) => item.ContactID === customer.contact_id)

          return contact ? { ...customer, account_name: contact.Name } : customer
        }),
      )
      swal.fire(
        'Xero contacts refreshed',
        `${renamedCustomers.length} account name${
          renamedCustomers.length === 1 ? '' : 's'
        } updated.`,
        'success',
      )
    } catch (error) {
      console.error('Failed to refresh Xero contacts:', error)
      swal.fire('Refresh failed', 'Could not refresh Xero contacts. Please try again.', 'error')
    } finally {
      setRefreshingXeroContacts(false)
    }
  }

  useEffect(() => {
    if (!refreshAvailableAt) return undefined

    const interval = setInterval(() => {
      if (Date.now() >= refreshAvailableAt) {
        setRefreshAvailableAt(0)
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [refreshAvailableAt])

  const refreshDisabled = loading || refreshingXeroContacts || refreshAvailableAt > Date.now()
  const refreshLabel =
    refreshAvailableAt > Date.now()
      ? `Available again ${new Date(refreshAvailableAt).toLocaleString()}`
      : refreshingXeroContacts
      ? 'Refreshing...'
      : 'Refresh Xero Contacts'

  const headers = [
    { label: 'ID', key: 'id' },
    { label: 'Customer Address ID', key: 'customer_address_id' },
    { label: 'Address', key: 'address' },
    { label: 'Street', key: 'street' },
    { label: 'City/Suburb', key: 'city' },
    { label: 'State', key: 'state' },
    { label: 'Post code', key: 'post_code' },
    { label: 'Delivery Zone Name', key: 'delivery_zone_name' },
    { label: 'Location Note', key: 'location_note' },
    { label: 'Xero Contact ID', key: 'p_location' },
    { label: 'Resident Contact', key: 'resident_contact' },
    { label: 'Resident Contact Two', key: 'resident_contact_two' },
    { label: 'Resident Name', key: 'resident_name' },
    { label: 'Resident Name Two', key: 'resident_name_two' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Updated At', key: 'updated_at' },
  ]

  const headerXero = [
    { label: 'Xero Contact ID', key: 'ContactID' },
    { label: 'Xero Account Code', key: 'AccountNumber' },
    { label: 'Contact Status', key: 'ContactStatus' },
    { label: 'Name', key: 'Name' },
    { label: 'First Name', key: 'FirstName' },
    { label: 'Last Name', key: 'LastName' },
    { label: 'Email Address', key: 'EmailAddress' },
    { label: 'Has Attachments', key: 'HasAttachments' },
    { label: 'Has Validation Errors', key: 'HasValidationErrors' },
    { label: 'Is Customer', key: 'IsCustomer' },
    { label: 'Is Supplier', key: 'IsSupplier' },
    { label: 'Payment Terms Sales Day', key: 'PaymentTerms.Sales.Day' },
    { label: 'Payment Terms Sales Type', key: 'PaymentTerms.Sales.Type' },
    { label: 'Phone DDI', key: 'Phones[0].PhoneNumber' },
    { label: 'Phone Default', key: 'Phones[1].PhoneNumber' },
    { label: 'Phone Fax', key: 'Phones[2].PhoneNumber' },
    { label: 'Phone Mobile', key: 'Phones[3].PhoneNumber' },
    { label: 'Address Street Type', key: 'Addresses[0].AddressType' },
    { label: 'Address Street City', key: 'Addresses[0].City' },
    { label: 'Address Street Region', key: 'Addresses[0].Region' },
    { label: 'Address POBOX Type', key: 'Addresses[1].AddressType' },
    { label: 'Address POBOX Line 1', key: 'Addresses[1].AddressLine1' },
    { label: 'Address POBOX City', key: 'Addresses[1].City' },
  ]

  const viewProduct_HTMLTable_Xero = [...xeroCustomerList]
    .sort((a, b) => a.Name.localeCompare(b.Name))
    .map((item, i) => {
      const matchingCustomer = customerList.find(
        (customer) => customer.contact_id === item.ContactID,
      )
      /*  console.log(matchingCustomer?.customer_address)
      console.log(
        'Contact id',
        item.ContactID,
        'Customer contact id',
        matchingCustomer?.customer_address.contact_id,
      ) */

      return (
        <tr className="small" key={item.ContactID}>
          <td>
            {i + 1} {/* {item.ContactID} */}
          </td>
          <td>{item.Name}</td>
          <td>{item.FirstName}</td>
          <td>{item.AccountNumber}</td>
          <td>
            {item.Phones && item.Phones.length > 0
              ? item.Phones.map((phone) => phone.PhoneNumber) // Extract PhoneNumber
                  .filter((number) => number && number.trim() !== '') // Filter out null, undefined, or empty strings
                  .join(' | ') // Join with "|"
              : 'No phone numbers available'}
          </td>

          <td style={{ width: '300px' }}>
            {matchingCustomer?.customer_address
              .filter((address) => address.p_location === item.ContactID)
              .map((address, index) => (
                <div key={index}>
                  {/* {console.log(address)} */}
                  <div>{address.address && <strong>{address.address}</strong>}</div>
                  {address.street && <div>{address.street}</div>}
                  {address.city && <div>{address.city}</div>}
                  {address.state && <div>{address.state}</div>}
                  {address.post_code && <div>{address.post_code}</div>}
                  {address?.delivery_zones?.name && <div>{address?.delivery_zones?.name}</div>}
                  {address.location_note && <div>{address.location_note}</div>}
                  {address.contacts && address.contacts.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>Contacts:</strong>
                      {address.contacts.map((contactObj, cIndex) => (
                        <div key={cIndex}>
                          {contactObj.contact_name} - {contactObj.contact_number}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* {(address.resident_name || address.resident_contact) && (
                    <div>
                      {address.resident_name} - {address.resident_contact}
                    </div>
                  )}
                  {(address.resident_name_two || address.resident_contact_two) && (
                    <div>
                      {address.resident_name_two} - {address.resident_contact_two}
                    </div>
                  )} */}
                </div>
              ))}
          </td>
          <td>
            <Link
              to={`/customers/update/${item.ContactID}`}
              className="btn btn-block btn-outline-info btn-sm me-2 mb-2"
            >
              <i className="far fa-edit"></i>
            </Link>
          </td>
        </tr>
      )
    })

  // Build CSV data from customerList
  const csvCustomerData = customerList.flatMap((customer) => {
    // Check if customer has any address
    if (customer.customer_address) {
      // Normalize customer_address to an array if it's not one
      const addresses = Array.isArray(customer.customer_address)
        ? customer.customer_address
        : [customer.customer_address]

      return addresses.flatMap((address) => {
        // If contacts exist and are an array with items
        if (Array.isArray(address.contacts) && address.contacts.length > 0) {
          return address.contacts.map((contact) => ({
            account_name: customer.account_name || '',
            contact_name: contact.contact_name || '',
            contact_number: contact.contact_number || '',
          }))
        }
        // If contacts exist as a single object (not an array) and not empty
        else if (
          address.contacts &&
          typeof address.contacts === 'object' &&
          Object.keys(address.contacts).length > 0
        ) {
          return [
            {
              account_name: customer.account_name || '',
              contact_name: address.contacts.contact_name || '',
              contact_number: address.contacts.contact_number || '',
            },
          ]
        }
        // If no contacts exist, output a row with empty contact details
        else {
          return [
            {
              account_name: customer.account_name || '',
              contact_name: '',
              contact_number: '',
            },
          ]
        }
      })
    }
    // If customer_address doesn't exist, still output a row with empty contacts
    return [
      {
        account_name: customer.account_name || '',
        contact_name: '',
        contact_number: '',
      },
    ]
  })

  const customerCsvHeaders = [
    { label: 'Account Name', key: 'account_name' },
    { label: 'Contact Name', key: 'contact_name' },
    { label: 'Contact Number', key: 'contact_number' },
  ]

  // Export all displayed data to an Excel workbook with multiple sheets
  const exportAllData = () => {
    try {
      const wb = XLSX.utils.book_new()

      // Xero contacts sheet
      const xeroSheetData = xeroCustomerList.map((item) => ({
        ContactID: item.ContactID || '',
        Name: item.Name || '',
        FirstName: item.FirstName || '',
        AccountNumber: item.AccountNumber || '',
        Phones:
          item.Phones && item.Phones.length
            ? item.Phones.map((p) => p.PhoneNumber)
                .filter(Boolean)
                .join(' | ')
            : '',
        EmailAddress: item.EmailAddress || '',
        AddressLine1:
          item.Addresses && item.Addresses[0]?.AddressLine1 ? item.Addresses[0].AddressLine1 : '',
        AddressCity: item.Addresses && item.Addresses[0]?.City ? item.Addresses[0].City : '',
        AddressRegion: item.Addresses && item.Addresses[0]?.Region ? item.Addresses[0].Region : '',
        POBoxLine1:
          item.Addresses && item.Addresses[1]?.AddressLine1 ? item.Addresses[1].AddressLine1 : '',
      }))

      const ws1 = XLSX.utils.json_to_sheet(xeroSheetData)
      XLSX.utils.book_append_sheet(wb, ws1, 'XeroContacts')

      // Customer contacts sheet (uses existing csvCustomerData)
      const ws2 = XLSX.utils.json_to_sheet(csvCustomerData)
      XLSX.utils.book_append_sheet(wb, ws2, 'CustomerContacts')

      // Addresses sheet built from customerList
      const addressRows = customerList.flatMap((customer) => {
        const addresses = Array.isArray(customer.customer_address)
          ? customer.customer_address
          : customer.customer_address
          ? [customer.customer_address]
          : []

        if (addresses.length === 0) {
          return [
            {
              account_name: customer.account_name || '',
              address: '',
              street: '',
              city: '',
              state: '',
              post_code: '',
              delivery_zone_name: '',
              p_location: '',
              resident_name: '',
              resident_contact: '',
              created_at: '',
              updated_at: '',
            },
          ]
        }

        return addresses.map((addr) => ({
          account_name: customer.account_name || '',
          address: addr.address || '',
          street: addr.street || '',
          city: addr.city || '',
          state: addr.state || '',
          post_code: addr.post_code || '',
          delivery_zone_name: addr?.delivery_zones?.name || '',
          p_location: addr.p_location || '',
          resident_name: addr.resident_name || '',
          resident_contact: addr.resident_contact || '',
          created_at: addr.created_at || '',
          updated_at: addr.updated_at || '',
        }))
      })

      const ws3 = XLSX.utils.json_to_sheet(addressRows)
      XLSX.utils.book_append_sheet(wb, ws3, 'Addresses')

      // Trigger download with the requested filename
      XLSX.writeFile(wb, 'NQL-Customers.xlsx')
    } catch (err) {
      console.error('Error exporting Excel:', err)
      swal.fire('Export error', 'Could not export data to Excel', 'error')
    }
  }

  return (
    <div className="content-wrapper">
      <nav aria-label="breadcrumb p-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <NavLink to="/">Dashboard</NavLink>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Customers
          </li>
        </ol>
      </nav>

      <h3 className="text-dark">All Customers</h3>

      {loadError && (
        <div
          className="alert alert-danger d-flex align-items-center justify-content-between"
          role="alert"
        >
          <span>{loadError}</span>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() => setRetryKey((key) => key + 1)}
          >
            Retry
          </button>
        </div>
      )}

      <div className="content">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title">All Customers</h5>
            {/* Export buttons */}
            <button
              onClick={exportAllData}
              className="btn btn-success"
              style={{ float: 'right', marginLeft: '10px' }}
              disabled={loading}
            >
              Download Excel
            </button>
            <button
              type="button"
              onClick={refreshXeroContacts}
              className="btn btn-info"
              style={{ float: 'right' }}
              disabled={refreshDisabled}
              title={refreshLabel}
            >
              <i
                className={`fas fa-sync-alt me-1${refreshingXeroContacts ? ' fa-spin' : ''}`}
                aria-hidden="true"
              ></i>
              {refreshLabel}
            </button>
          </div>

          <div className="card-body" style={{ fontSize: '12px', paddingTop: '5px' }}>
            <table id="dataTable" className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Account Name</th>
                  <th>Account Contact Name</th>
                  <th>Code</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 10 }).map((_, idx) => (
                      <tr key={idx}>
                        <td>
                          <Skeleton width={20} />
                        </td>{' '}
                        {/* “#” */}
                        <td>
                          <Skeleton width={180} />
                        </td>{' '}
                        {/* Account Name */}
                        <td>
                          <Skeleton width={150} />
                        </td>{' '}
                        {/* Contact Name */}
                        <td>
                          <Skeleton width={60} />
                        </td>{' '}
                        {/* Code */}
                        <td>
                          <Skeleton width={140} />
                        </td>{' '}
                        {/* Phone */}
                        <td>
                          <Skeleton width="100%" />
                        </td>{' '}
                        <td>
                          <Skeleton width={70} height={24} />
                        </td>
                        {/* Action btn */}
                      </tr>
                    ))
                  : viewProduct_HTMLTable_Xero}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Customers
